// 📄 pages/Proyectos/components/ProveedorDetallePanel.tsx
// Panel deslizable con el detalle completo de UN proveedor (ver
// BuscarProveedores.tsx) — calificaciones, cuestionarios y campos
// personalizados vienen anidados en la respuesta real de Ariba
// (qualifications[]/questionnaires[]/genericCustomFields), así que se
// muestran en secciones separadas en vez de intentar meterlos en la
// tarjeta resumen (ProveedorCard.tsx). Incluye un botón "Ver JSON
// completo" para cualquier campo que Ariba devuelva y esta pantalla
// todavía no modele explícitamente — nunca se pierde información.
import { useState } from 'react';
import { X, MapPin, Mail, User, Calendar, ClipboardCheck, FileQuestion, Braces, Copy, Check } from 'lucide-react';
import { Proveedor } from '../../../services/slpProveedoresService.ts';

interface ProveedorDetallePanelProps {
  proveedor: Proveedor;
  onClose: () => void;
}

const esVerdadero = (valor?: string) => (valor || '').toLowerCase() === 'true';

// Los timestamps de Ariba vienen como epoch-ms EN STRING ("1764193604632")
// — "0" significa "nunca" (no una fecha real del 01/01/1970).
const formatearFecha = (valor?: string): string | null => {
  if (!valor || valor === '0') return null;
  const ms = Number(valor);
  if (!Number.isFinite(ms) || ms <= 0) return null;
  try {
    return new Date(ms).toLocaleDateString('es-ES', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return null;
  }
};

const Badge = ({ children, tono = 'neutral' }: { children: React.ReactNode; tono?: 'neutral' | 'ok' | 'warn' | 'bad' }) => {
  const clases = {
    neutral: 'text-gray-400 bg-white/5 border-white/10',
    ok: 'text-emerald-300 bg-emerald-500/10 border-emerald-500/30',
    warn: 'text-amber-300 bg-amber-500/10 border-amber-500/30',
    bad: 'text-red-300 bg-red-500/10 border-red-500/30'
  }[tono];
  return <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${clases}`}>{children}</span>;
};

const Campo = ({ label, valor }: { label: string; valor?: string | null }) => {
  if (!valor) return null;
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wider text-gray-500">{label}</p>
      <p className="text-sm text-gray-200">{valor}</p>
    </div>
  );
};

export default function ProveedorDetallePanel({ proveedor, onClose }: ProveedorDetallePanelProps) {
  const [mostrarJson, setMostrarJson] = useState(false);
  const [copiado, setCopiado] = useState(false);

  const nombre = proveedor['Supplier Name'] || 'Proveedor sin nombre';
  const direccion = [proveedor['Address - Line1'], proveedor['Address - City'], proveedor['Address - Country Code'], proveedor['Address - Postal Code']]
    .filter(Boolean)
    .join(', ');
  const contactoNombre = [proveedor['Primary contact first name'], proveedor['Primary contact last name']].filter(Boolean).join(' ');

  const calificaciones: any[] = Array.isArray(proveedor.qualifications) ? proveedor.qualifications : [];
  const cuestionarios: any[] = Array.isArray(proveedor.questionnaires) ? proveedor.questionnaires : [];
  const businessPartnerCustomFields: any[] = Array.isArray(proveedor.genericCustomFields?.businessPartnerGenericCustomField)
    ? proveedor.genericCustomFields.businessPartnerGenericCustomField
    : [];
  const supplierCustomFields: any[] = Array.isArray(proveedor.genericCustomFields?.supplierGenericCustomField)
    ? proveedor.genericCustomFields.supplierGenericCustomField
    : [];

  const handleCopiarJson = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(proveedor, null, 2));
      setCopiado(true);
      setTimeout(() => setCopiado(false), 1500);
    } catch (err) {
      console.error('Error al copiar el JSON:', err);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex justify-end" onClick={onClose}>
      <div
        className="w-full max-w-lg h-full bg-[rgb(var(--c-surface))] border-l border-white/10 shadow-2xl overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 flex items-start justify-between gap-3 p-5 border-b border-white/10 bg-[rgb(var(--c-surface))]">
          <div className="min-w-0">
            <h3 className="text-lg font-semibold text-white truncate">{nombre}</h3>
            <p className="text-xs text-gray-500 font-mono mt-0.5">{proveedor['SM Vendor ID']}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white flex-shrink-0">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-6">
          <div className="flex flex-wrap gap-1.5">
            {proveedor['Registration Status'] && <Badge tono={proveedor['Registration Status'] === 'Registered' ? 'ok' : 'warn'}>{proveedor['Registration Status']}</Badge>}
            {proveedor['Qualification Status'] && <Badge tono={proveedor['Qualification Status'] === 'Qualified' ? 'ok' : 'warn'}>{proveedor['Qualification Status']}</Badge>}
            {esVerdadero(proveedor['Blocked Status']) && <Badge tono="bad">Bloqueado</Badge>}
            {esVerdadero(proveedor['Approved']) && <Badge tono="ok">Aprobado</Badge>}
            {proveedor['Integrated to ERP'] && <Badge tono={proveedor['Integrated to ERP'] === 'Error' ? 'bad' : 'ok'}>ERP: {proveedor['Integrated to ERP']}</Badge>}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Campo label="SM Vendor ID" valor={proveedor['SM Vendor ID']} />
            <Campo label="ERP Vendor ID" valor={proveedor['ERP Vendor ID']} />
            <Campo label="An ID" valor={proveedor['An Id']} />
            <Campo label="ACM ID" valor={proveedor['ACM ID']} />
            <Campo label="Sistema origen" valor={proveedor['Source System']} />
            <Campo label="Supplier Manager" valor={proveedor['Primary Supplier Manager']} />
          </div>

          {direccion && (
            <div>
              <h4 className="text-xs font-semibold text-gray-300 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                <MapPin className="w-3.5 h-3.5 text-indigo-400" /> Dirección
              </h4>
              <p className="text-sm text-gray-300">{direccion}</p>
            </div>
          )}

          {(contactoNombre || proveedor['Primary contact email']) && (
            <div>
              <h4 className="text-xs font-semibold text-gray-300 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                <User className="w-3.5 h-3.5 text-indigo-400" /> Contacto principal
              </h4>
              {contactoNombre && <p className="text-sm text-gray-300">{contactoNombre}</p>}
              {proveedor['Primary contact email'] && (
                <p className="text-sm text-gray-400 flex items-center gap-1.5 mt-0.5">
                  <Mail className="w-3.5 h-3.5 flex-shrink-0" /> {proveedor['Primary contact email']}
                </p>
              )}
            </div>
          )}

          {(formatearFecha(proveedor['Record Created Date']) || formatearFecha(proveedor['Last Update Date'])) && (
            <div>
              <h4 className="text-xs font-semibold text-gray-300 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                <Calendar className="w-3.5 h-3.5 text-indigo-400" /> Fechas
              </h4>
              <div className="grid grid-cols-2 gap-3">
                <Campo label="Creado" valor={formatearFecha(proveedor['Record Created Date'])} />
                <Campo label="Última actualización" valor={formatearFecha(proveedor['Last Update Date'])} />
                <Campo label="Última revisión" valor={formatearFecha(proveedor['Last Review Date'])} />
                <Campo label="Último cambio de estado" valor={formatearFecha(proveedor['Last Status Change Date'])} />
              </div>
            </div>
          )}

          {calificaciones.length > 0 && (
            <div>
              <h4 className="text-xs font-semibold text-gray-300 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                <ClipboardCheck className="w-3.5 h-3.5 text-indigo-400" /> Calificaciones ({calificaciones.length})
              </h4>
              <div className="space-y-1.5 max-h-64 overflow-y-auto pr-1">
                {calificaciones.map((c, i) => (
                  <div key={i} className="bg-white/5 border border-white/10 rounded-lg p-2.5 flex items-center justify-between gap-2 text-xs">
                    <div className="min-w-0">
                      <p className="text-gray-200 font-medium truncate">
                        {c.Category || 'Sin categoría'} {c.Region ? `· ${c.Region}` : ''} {c['Business Unit'] ? `· BU ${c['Business Unit']}` : ''}
                      </p>
                      {c['Process Type'] && <p className="text-gray-500 truncate">{c['Process Type']}</p>}
                    </div>
                    {c['Qualification Status'] && (
                      <span className="flex-shrink-0"><Badge tono={c['Qualification Status'] === 'Qualified' ? 'ok' : c['Qualification Status'] === 'Expired' ? 'bad' : 'neutral'}>{c['Qualification Status']}</Badge></span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {cuestionarios.length > 0 && (
            <div>
              <h4 className="text-xs font-semibold text-gray-300 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                <FileQuestion className="w-3.5 h-3.5 text-indigo-400" /> Cuestionarios ({cuestionarios.length})
              </h4>
              <div className="space-y-1.5 max-h-64 overflow-y-auto pr-1">
                {cuestionarios.map((q, i) => (
                  <div key={i} className="bg-white/5 border border-white/10 rounded-lg p-2.5 text-xs">
                    <p className="text-gray-200 font-medium">{q.questionnaireTitle || q.questionnaireId}</p>
                    <p className="text-gray-500">{q.workspaceType || 'Sin tipo'} {q.matrixInfo?.Status ? `· Estado: ${q.matrixInfo.Status}` : ''}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {(businessPartnerCustomFields.length > 0 || supplierCustomFields.length > 0) && (
            <div>
              <h4 className="text-xs font-semibold text-gray-300 uppercase tracking-wider mb-2">Campos personalizados</h4>
              <div className="space-y-1.5">
                {[...businessPartnerCustomFields, ...supplierCustomFields].map((f, i) => (
                  <p key={i} className="text-xs text-gray-400">
                    <span className="text-gray-500">{f.name}:</span> <span className="text-gray-200">{f.content}</span>
                  </p>
                ))}
              </div>
            </div>
          )}

          <div className="pt-2 border-t border-white/10">
            <button
              type="button"
              onClick={() => setMostrarJson((v) => !v)}
              className="flex items-center gap-1.5 text-xs font-semibold text-gray-400 hover:text-white transition-colors"
            >
              <Braces className="w-3.5 h-3.5" /> {mostrarJson ? 'Ocultar' : 'Ver'} JSON completo
            </button>
            {mostrarJson && (
              <div className="relative mt-2">
                <button
                  type="button"
                  onClick={handleCopiarJson}
                  title="Copiar JSON"
                  className="absolute top-2 right-2 p-1.5 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                >
                  {copiado ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                </button>
                <pre className="bg-black/30 border border-white/10 rounded-lg p-3 text-[11px] text-gray-300 overflow-x-auto max-h-80 overflow-y-auto">
                  {JSON.stringify(proveedor, null, 2)}
                </pre>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
